<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
const props = defineProps({
  categories: { type: Array, default: () => [] },
  performAction: { type: Function, required: true },
});
const { t } = useI18n();
const categoryName = ref('');
const categoryError = ref('');
const editingCategoryId = ref(null);
const editingCategoryName = ref('');
const createCategory = async () => {
  categoryError.value = '';
  if (!categoryName.value.trim()) {
    categoryError.value = t('INTERNAL_CHAT.CATEGORY_NAME_REQUIRED');
    return;
  }
  if (
    await props.performAction('createCategory', {
      name: categoryName.value.trim(),
    })
  )
    categoryName.value = '';
};
const startCategoryEdit = category => {
  editingCategoryId.value = category.id;
  editingCategoryName.value = category.name;
};
const saveCategoryEdit = async category => {
  if (!editingCategoryName.value.trim()) return;
  if (
    await props.performAction('updateCategory', {
      categoryId: category.id,
      attributes: { name: editingCategoryName.value.trim() },
    })
  ) {
    editingCategoryId.value = null;
    editingCategoryName.value = '';
  }
};
</script>

<template>
  <section class="border-t border-n-weak pt-4">
    <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
      {{ t('INTERNAL_CHAT.CATEGORIES') }}
    </h3>
    <div class="mb-3 flex items-center gap-2">
      <label class="sr-only" for="internal-chat-category-name">
        {{ t('INTERNAL_CHAT.CATEGORY_NAME') }}
      </label>
      <Input
        id="internal-chat-category-name"
        v-model="categoryName"
        size="sm"
        class="min-w-0 flex-1"
        :placeholder="t('INTERNAL_CHAT.CATEGORY_NAME')"
      />
      <Button
        type="button"
        size="sm"
        variant="faded"
        color="slate"
        :label="t('INTERNAL_CHAT.ADD')"
        @click="createCategory"
      />
    </div>
    <p v-if="categoryError" class="mb-2 text-xs text-n-ruby-11" role="alert">
      {{ categoryError }}
    </p>
    <ul class="m-0 grid gap-1 p-0">
      <li
        v-for="category in categories"
        :key="category.id"
        class="flex items-center justify-between gap-3 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
      >
        <template v-if="editingCategoryId === category.id">
          <label
            class="sr-only"
            :for="`internal-chat-category-edit-${category.id}`"
          >
            {{ t('INTERNAL_CHAT.CATEGORY_NAME') }}
          </label>
          <Input
            :id="`internal-chat-category-edit-${category.id}`"
            v-model="editingCategoryName"
            size="sm"
            class="min-w-0 flex-1"
          />
          <Button
            type="button"
            size="xs"
            :label="t('INTERNAL_CHAT.SAVE')"
            @click="saveCategoryEdit(category)"
          />
        </template>
        <template v-else>
          <span class="min-w-0 truncate">{{ category.name }}</span>
          <div class="flex shrink-0 gap-2">
            <Button
              type="button"
              variant="ghost"
              color="slate"
              size="xs"
              :label="t('INTERNAL_CHAT.EDIT')"
              @click="startCategoryEdit(category)"
            />
            <Button
              type="button"
              variant="ghost"
              color="ruby"
              size="xs"
              :label="t('INTERNAL_CHAT.REMOVE')"
              @click="performAction('deleteCategory', category.id)"
            />
          </div>
        </template>
      </li>
    </ul>
  </section>
</template>
